import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { rollbackReview } from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { eq } from "@-/com.npmjs.drizzle-orm";

/** Identifies the review to undo — the pair written together by `persistStudyCardReview`. */
export type StudyCardReviewUndoInput = {
  fsrsCardId: string;
  /** The `user_card_review` row recording this review, whose log carries the pre-review state. */
  userCardReviewId: string;
};

/**
 * Atomically undo one recorded review, restoring its FSRS card to the state it had before.
 *
 * The exact inverse of `persistStudyCardReview`, and deliberately its mirror image: the card is
 * rebuilt INSIDE the transaction from the `fsrs_card` row as committed, never from a render-time
 * snapshot. Undo runs from a screen whose cached card can easily lag the rating that just landed,
 * and rolling back a stale snapshot would restore values that were never current.
 *
 * Both rows the review wrote are soft-deleted rather than dropped: these tables sync, and a hard
 * delete would have to race the change feed to be replicated as one. Soft deletion also keeps the
 * undo itself auditable. Every read of review history already filters `deleted_at IS NULL` (the
 * soft-delete proxy injects it), so an undone review stops counting toward review totals without
 * those call sites needing to know undo exists.
 *
 * Returns `null` when there is nothing to undo — the review row is missing or already undone — so
 * a double-tap of the back button is a no-op rather than a thrown error or a double rollback.
 */
export async function undoStudyCardReview({
  actorDb,
  fsrsCardId,
  now,
  userCardReviewId,
}: StudyCardReviewUndoInput & {
  actorDb: ActorDatabaseHandle;
  now: Date;
}): Promise<{ undone: true } | null> {
  const nowIso = now.toISOString();

  return actorDb.transaction(async (tx) => {
    const userCardReview = await tx.query.userCardReview.findFirst({
      where: { id: { eq: userCardReviewId } },
    });
    // Already undone (or never existed): the soft-delete proxy hides deleted rows from this read,
    // so a second undo of the same review finds nothing and stops here.
    if (userCardReview === undefined) {
      return null;
    }

    const reviewLog = await tx.query.fsrsCardReviewLog.findFirst({
      where: { id: { eq: userCardReview.fsrsCardReviewLogId } },
    });
    if (reviewLog === undefined) {
      throw new Error(
        `Expected the review log for an undone review, found none (${userCardReview.fsrsCardReviewLogId})`,
      );
    }

    const fsrsCard = await tx.query.fsrsCard.findFirst({
      where: { id: { eq: fsrsCardId } },
    });
    if (fsrsCard === undefined) {
      throw new Error(`Expected an FSRS card to roll back, found none (${fsrsCardId})`);
    }

    const updateResult = await tx
      .update(actorSchema.fsrsCard)
      .set(rollbackReview(fsrsCard, reviewLog, now))
      .where(eq(actorSchema.fsrsCard.id, fsrsCardId))
      .run();

    if (updateResult.changes !== 1) {
      throw new Error(
        `Expected to roll back exactly one FSRS card, updated ${updateResult.changes}`,
      );
    }

    await tx
      .update(actorSchema.userCardReview)
      .set({ deletedAt: nowIso })
      .where(eq(actorSchema.userCardReview.id, userCardReviewId))
      .run();

    await tx
      .update(actorSchema.fsrsCardReviewLog)
      .set({ deletedAt: nowIso })
      .where(eq(actorSchema.fsrsCardReviewLog.id, reviewLog.id))
      .run();

    return { undone: true };
  });
}
